'use client'

import type {
	SRT_Column,
	SRT_Localization,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import React from 'react'
import { GripVerticalIcon } from 'lucide-react'

import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Sortable,
	SortableContent,
	SortableItem,
	SortableItemHandle,
	SortableOverlay,
} from '@/shared/components/ui/sortable'

import { cn } from '../../lib/utils'
import { flexRender } from '../../utils/tanstack.helpers'

export const SRT_TableColumnsReorderingList = <T extends SRT_RowData>({
	search,
	table,
}: ColumnsReorderingListProps<T>) => {
	const columns = table.getAllLeafColumns()
	const items = columns.map((column) => ({ id: column.id, column }))
	const localization = table.options.localization

	const isMatchSearch = (column: SRT_Column<T>) => {
		if (
			search &&
			column.columnDef.header &&
			typeof column.columnDef.header === 'string'
		) {
			return column.columnDef.header
				.toLocaleLowerCase()
				.includes(search.toLocaleLowerCase())
		}

		return false
	}

	return (
		<Sortable
			value={items}
			onValueChange={(newOrder) =>
				table.setColumnOrder(newOrder.map(({ id }) => id))
			}
			getItemValue={(item) => item.id}
		>
			<SortableContent asChild>
				<div>
					{items.map(({ id, column }) => (
						<SortableItem
							key={id}
							value={id}
							disabled={!!column.getIsPinned()}
							asChild
						>
							<div
								data-col-name={flexRender(column.columnDef.header, {})}
								className={cn(
									'flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-accent focus:bg-accent',
									{
										'bg-orange-50 dark:bg-orange-950': isMatchSearch(column),
									}
								)}
							>
								<ColumnItem column={column} localization={localization} />
								<SortableItemHandle>
									<GripVerticalIcon className='size-4 cursor-grab text-disabled-foreground' />
								</SortableItemHandle>
							</div>
						</SortableItem>
					))}
				</div>
			</SortableContent>
			<SortableOverlay>
				{({ value }) => {
					const activeItem = items.find((item) => item.id === value)
					if (!activeItem) {
						return null
					}

					return (
						<div className='flex cursor-grabbing items-center justify-between rounded-md bg-accent px-2 py-1.5 opacity-75'>
							<ColumnItem
								column={activeItem.column}
								localization={localization}
							/>
						</div>
					)
				}}
			</SortableOverlay>
		</Sortable>
	)
}

type ColumnsReorderingListProps<T extends SRT_RowData> = {
	table: SRT_TableInstance<T>
	search?: string
}

type ColumnItemProps<T extends SRT_RowData> = {
	column: SRT_Column<T>
	localization: SRT_Localization
}

const ColumnItem = <T extends SRT_RowData>({
	column,
	localization,
}: ColumnItemProps<T>) => (
	<div className='w-full flex-col items-center justify-between'>
		<label className='flex items-center space-x-2' key={column.id}>
			<Checkbox
				checked={column.getIsVisible()}
				disabled={!column.getCanHide()}
				onChange={column.getToggleVisibilityHandler()}
			/>
			<div>
				<span className='text-sm font-normal select-none'>
					{flexRender(column.columnDef.header, {})}
					{column.getIsPinned() && (
						<span className='text-muted-foreground'>
							{' '}
							({localization?.pinned || 'Pinned'})
						</span>
					)}
				</span>
				<div className='text-sm text-muted-foreground'>
					{column.id === 'srt-row-spacer'
						? 'Spacer'
						: flexRender(column?.parent?.columnDef?.header, {})}
				</div>
			</div>
		</label>
	</div>
)
